import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  LayoutDashboard, Mail, Users, Coins, CreditCard, Sparkles, Tags,
  FileText, HelpCircle, Settings, LogOut, Menu, X, ArrowLeft, FileStack, ShieldAlert,
  MessageSquareWarning, Gift,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ADMIN } from "@/constants/testIds";

const NAV = [
  { to: "/admin", label: "Pulpit", icon: LayoutDashboard, testId: ADMIN.navDashboard, end: true },
  { to: "/admin/wiadomosci", label: "Wiadomości", icon: Mail, testId: ADMIN.navMessages },
  { to: "/admin/uzytkownicy", label: "Użytkownicy", icon: Users, testId: ADMIN.navUsers },
  { to: "/admin/kredyty", label: "Kredyty", icon: Coins, testId: ADMIN.navCredits },
  { to: "/admin/platnosci", label: "Płatności", icon: CreditCard, testId: ADMIN.navPayments },
  { to: "/admin/operacje-ai", label: "Operacje AI", icon: Sparkles, testId: ADMIN.navAi },
  { to: "/admin/braki-asystenta", label: "Braki asystenta", icon: HelpCircle, testId: "admin-nav-no-match" },
  { to: "/admin/ceny", label: "Ceny i modele", icon: Tags, testId: ADMIN.navPricing },
  { to: "/admin/kontrola-kosztow", label: "Kontrola kosztów AI", icon: ShieldAlert, testId: "admin-nav-aicost" },
  { to: "/admin/tresci", label: "Treści i prompty", icon: FileText, testId: ADMIN.navContent },
  { to: "/admin/cms-promptow", label: "CMS promptów", icon: FileStack, testId: ADMIN.navCms },
  { to: "/admin/pomoc", label: "FAQ i pomoc", icon: HelpCircle, testId: ADMIN.navHelp },
  { to: "/admin/zgloszenia", label: "Zgłoszenia", icon: MessageSquareWarning, testId: "admin-nav-feedback" },
  { to: "/admin/bonus-powitalny", label: "Bonus powitalny", icon: Gift, testId: "admin-nav-welcome-bonus" },
  { to: "/admin/ustawienia", label: "Ustawienia", icon: Settings, testId: ADMIN.navSettings },
];

function Nav({ onNavigate, messageCount }) {
  const cls = ({ isActive }) =>
    `flex items-center gap-3 rounded-field px-3 py-2.5 text-[15px] font-medium transition-colors duration-200 ${
      isActive ? "bg-brand-accent text-brand-on" : "text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
    }`;
  return (
    <nav className="flex h-full flex-col">
      {NAV.map(({ to, label, icon: Icon, testId, end }) => (
        <NavLink key={to} to={to} end={end} data-testid={testId} onClick={onNavigate} className={cls}>
          <Icon size={18} strokeWidth={1.75} />
          <span className="flex-1">{label}</span>
          {to === "/admin/wiadomosci" && messageCount > 0 && (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-brand-accent px-1.5 text-[10px] font-bold text-brand-on">
              {messageCount}
            </span>
          )}
        </NavLink>
      ))}
      <div className="mt-auto pt-6">
        <Link
          to="/biblioteka"
          onClick={onNavigate}
          className="flex items-center gap-3 rounded-field px-3 py-2.5 text-[15px] font-medium text-brand-sec hover:bg-brand-elevated hover:text-brand-text"
        >
          <ArrowLeft size={18} strokeWidth={1.75} /> Powrót do aplikacji
        </Link>
      </div>
    </nav>
  );
}

export function AdminShell({ children, messageCount = 0 }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/logowanie", { replace: true });
  };

  return (
    <div data-testid={ADMIN.shell} className="min-h-screen bg-brand-bg lg:flex">
      <aside className="hidden w-[260px] shrink-0 flex-col border-r border-brand-line bg-brand-surface px-4 py-6 lg:flex">
        <div className="mb-6 flex items-center gap-2 px-2">
          <span className="font-display text-lg font-extrabold tracking-tight text-brand-text">
            PROMPTIVO<span className="text-brand-accent">.</span>
          </span>
          <span className="rounded-full bg-brand-elevated px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-brand-accent">
            Admin
          </span>
        </div>
        <div className="flex-1 overflow-y-auto">
          <Nav messageCount={messageCount} />
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-brand-line bg-brand-bg/95 px-4 backdrop-blur md:px-8">
          <button
            type="button"
            aria-label="Menu"
            onClick={() => setOpen(true)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-field border border-brand-line text-brand-text lg:hidden"
          >
            <Menu size={20} strokeWidth={1.75} />
          </button>
          <div className="ml-auto flex items-center gap-3">
            <span className="hidden text-sm text-brand-sec sm:inline">{user?.email}</span>
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-elevated text-brand-accent">
              {(user?.first_name || user?.email || "A").charAt(0).toUpperCase()}
            </span>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex h-9 items-center gap-1.5 rounded-field border border-brand-line px-3 text-sm text-brand-sec hover:text-brand-text"
            >
              <LogOut size={15} strokeWidth={1.75} /> Wyloguj
            </button>
          </div>
        </header>
        <main className="min-w-0 flex-1 px-4 py-8 md:px-8 lg:py-10">{children}</main>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div className="fixed inset-0 z-50 lg:hidden" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
            <motion.aside
              className="absolute left-0 top-0 flex h-full w-[280px] flex-col overflow-y-auto bg-brand-surface p-4"
              initial={{ x: -300 }} animate={{ x: 0 }} exit={{ x: -300 }} transition={{ type: "tween", duration: 0.25 }}
            >
              <div className="mb-5 flex items-center justify-between px-2">
                <span className="font-display text-lg font-extrabold text-brand-text">PROMPTIVO<span className="text-brand-accent">.</span></span>
                <button type="button" aria-label="Zamknij" onClick={() => setOpen(false)} className="inline-flex h-10 w-10 items-center justify-center rounded-field border border-brand-line text-brand-text">
                  <X size={20} strokeWidth={1.75} />
                </button>
              </div>
              <Nav messageCount={messageCount} onNavigate={() => setOpen(false)} />
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
